import { useEffect, useState } from "react";
import "@/App.css";
import { translations } from "./i18n/translations";
import SiteHeader from "./components/SiteHeader";
import Hero from "./components/Hero";
import Services from "./components/Services";
import WhyUs from "./components/WhyUs";
import InsurerPartnersHomeSection from "./components/InsurerPartnersHomeSection";
import Brands from "./components/Brands";
import Booking from "./components/Booking";
import Contact from "./components/Contact";
import PilkingtonPage from "./components/PilkingtonPage";
import ClimatePage from "./components/ClimatePage";
import OilsPage from "./components/OilsPage";
import PaintPage from "./components/PaintPage";
import ClaimsPage from "./components/ClaimsPage";
import MechanicsPage from "./components/MechanicsPage";
import DiagnosticsPage from "./components/DiagnosticsPage";
import ItpPage from "./components/ItpPage";
import TachographPage from "./components/TachographPage";
import Footer from "./components/Footer";
import TermsPage from "./components/TermsPage";
import WhatsAppFloat from "./components/WhatsAppFloat";
import CookieBanner from "./components/CookieBanner";
import ServiceAdjacentNav from "./components/ServiceAdjacentNav";
import ServiceHomeBreadcrumb from "./components/ServiceHomeBreadcrumb";
import { trackEvent, canTrackCategory, getTrackingApiBaseUrl } from "@/utils/tracking";
import { SITE_BRAND_DISPLAY_NAME, SITE_ORIGIN_CANONICAL } from "@/config/siteBrand";
import { socialPreviewByPageKey } from "@/config/socialPreviewByPage";
import siteRoutesManifest from "@/config/siteRoutes.manifest.json";
import { LEGACY_PATH_REDIRECTS } from "@/config/legacyPathRedirects";
import NotFoundPage from "./components/NotFoundPage";
import VinPartsPage from "./components/vinParts/VinPartsPage";
import PortalEntryPage from "./components/PortalEntryPage";
import PortalLoginPage from "./components/portal/PortalLoginPage";

const KNOWN_SPA_PATHS = new Set(siteRoutesManifest.map((r) => r.path));

function getPhpPortalLoginUrls() {
  const configuredAdmin = process.env.REACT_APP_ADMIN_URL || "";
  const configuredDesk = process.env.REACT_APP_DESK_URL || "";
  const isLocalDevHost = ["localhost", "127.0.0.1"].includes(window.location.hostname);
  // Migrated portal: keep /admin and /desk handled by React (not PHP pages).
  const adminUrl = configuredAdmin
    || (isLocalDevHost ? "http://localhost:3000/admin" : `${window.location.origin}/admin`);
  const deskUrl = configuredDesk
    || (isLocalDevHost ? "http://localhost:3000/desk" : `${window.location.origin}/desk`);
  return { adminUrl, deskUrl };
}

/** Lowercases, trims trailing slashes, strips optional `/ro/` hosting prefix. */
function normalizeSpaPath(rawPathname) {
  let p = String(rawPathname || "").toLowerCase().trim();
  if (!p || p === "/") return "/";
  p = p.replace(/\/+$/, "") || "/";
  if (p === "/ro") return "/";
  if (p.startsWith("/ro/")) {
    const rest = p.slice(4);
    p = rest ? `/${rest.replace(/^\/+/, "")}` : "/";
    p = p.replace(/\/+$/, "") || "/";
  }
  return p;
}

function isPlainObject(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function deepMerge(base, override) {
  if (!isPlainObject(base) || !isPlainObject(override)) return override ?? base;
  const merged = { ...base };
  Object.keys(override).forEach((key) => {
    const baseValue = base[key];
    const overrideValue = override[key];
    if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
      merged[key] = deepMerge(baseValue, overrideValue);
    } else {
      merged[key] = overrideValue;
    }
  });
  return merged;
}

function upsertMetaTag(key, value, content) {
  let tag = document.querySelector(`meta[${key}="${value}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(key, value);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}

function upsertLinkTag(rel, href, hreflang = null) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let tag = document.querySelector(selector);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    if (hreflang) tag.setAttribute("hreflang", hreflang);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

function removeOgImageDimensionTags() {
  document.querySelector('meta[property="og:image:width"]')?.remove();
  document.querySelector('meta[property="og:image:height"]')?.remove();
}

/** Per-route share images; drops static width/height from index.html when aspect ratio differs. */
function applySocialPreviewMeta(originCanonical, pageKey) {
  const preview = socialPreviewByPageKey[pageKey] || socialPreviewByPageKey.home;
  const imageUrl = `${originCanonical}${preview.imagePath}`;
  removeOgImageDimensionTags();
  upsertMetaTag("property", "og:image", imageUrl);
  upsertMetaTag("property", "og:image:alt", preview.alt);
  upsertMetaTag("name", "twitter:image", imageUrl);
  upsertMetaTag("name", "twitter:image:alt", preview.alt);
}

function App() {
  const [lang, setLang] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const langFromQuery = params.get("lang");
    if (langFromQuery && translations[langFromQuery]) {
      return langFromQuery;
    }
    const savedLang = window.localStorage.getItem("site_lang");
    if (savedLang && translations[savedLang]) {
      return savedLang;
    }
    return "ro";
  });
  const [cookieOpenSignal, setCookieOpenSignal] = useState(0);
  const t = deepMerge(translations.ro, translations[lang] || {});
  const appPortalCopy = translations[lang]?.appPortal || translations.ro.appPortal;
  const pathname = normalizeSpaPath(window.location.pathname);
  const isVinPartsToolRoute =
    pathname === "/piese-dupa-vin" || pathname.startsWith("/piese-dupa-vin/");
  const isDeskRoute = pathname === "/desk" || pathname.startsWith("/desk/");
  const isAdminRoute =
    pathname === "/admin" || pathname.startsWith("/admin/");
  const isAppPortalRoute = pathname === "/app";
  const legacyRedirectTarget = LEGACY_PATH_REDIRECTS[pathname] || null;
  const isKnownSpaRoute =
    KNOWN_SPA_PATHS.has(pathname) || isVinPartsToolRoute || isAppPortalRoute || !!legacyRedirectTarget;
  const isNotFound =
    !isAdminRoute && !isDeskRoute && !isKnownSpaRoute;
  const isPilkingtonPage = pathname === "/parbrize-pilkington" || pathname === "/pilkington";
  const isClimatePage = pathname === "/climatizare-auto" || pathname === "/climatizare";
  const isOilsPage = pathname === "/uleiuri-consultanta" || pathname === "/uleiuri-auto";
  const isPaintPage = pathname === "/vopsitorie-auto" || pathname === "/vopsitorie";
  const isClaimsPage = pathname === "/constatari-daune" || pathname === "/daune-auto";
  const isMechanicsPage = pathname === "/mecanica-revizii" || pathname === "/mecanica";
  const isDiagnosticsPage = pathname === "/diagnoza-computerizata" || pathname === "/diagnoza";
  const isItpPage = pathname === "/itp-auto-camioane" || pathname === "/itp";
  const isTachographPage = pathname === "/verificare-tahograf" || pathname === "/tahograf";
  const isTermsPage = pathname === "/termeni-si-conditii";

  const isServiceDetailPage =
    isPilkingtonPage ||
    isMechanicsPage ||
    isDiagnosticsPage ||
    isClimatePage ||
    isOilsPage ||
    isPaintPage ||
    isItpPage ||
    isTachographPage ||
    isClaimsPage;

  const reopenCookieSettings = () => setCookieOpenSignal((n) => n + 1);

  useEffect(() => {
    if (!legacyRedirectTarget) return;
    const search = window.location.search || "";
    const hash = legacyRedirectTarget.includes("#") ? "" : window.location.hash || "";
    window.location.replace(`${legacyRedirectTarget}${search}${hash}`);
  }, [legacyRedirectTarget]);

  useEffect(() => {
    window.localStorage.setItem("site_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const baseUrl = SITE_ORIGIN_CANONICAL;

    const seoByPage = {
      home: {
        ro: {
          title: `${SITE_BRAND_DISPLAY_NAME} - Service auto multimarca, ITP & Tahograf · Slobozia, Ialomita`,
          description:
            "Service auto multimarcă în Slobozia, Ialomița din 2014. ITP auto și camioane, verificare tahograf, mecanică, diagnoză, tinichigerie & vopsitorie, parbrize Pilkington, constatări daune.",
        },
        en: {
          title: `${SITE_BRAND_DISPLAY_NAME} - Multi-brand auto service in Slobozia`,
          description:
            "Multi-brand auto service in Slobozia since 2014: PTI, tachograph checks, mechanics, diagnostics, bodywork, Pilkington windshields and claims support.",
        },
        uk: {
          title: `${SITE_BRAND_DISPLAY_NAME} - Мультимарковий автосервіс у Слобозії`,
          description:
            "Мультимарковий автосервіс у Слобозії з 2014 року: ITP, тахограф, механіка, діагностика, кузовні роботи, автоскло Pilkington та врегулювання збитків.",
        },
      },
      pilkington: {
        ro: {
          title:
            "Parbrize Pilkington — înlocuire, montaj & reparație ciobitură · Slobozia, Ialomița | Selena Service",
          description:
            "Pilkington Team Partner: înlocuire parbriz și geamuri, montaj profesionist și reparație lovitură de piatră la Selena Service în Slobozia, județul Ialomița. Suport CASCO și flote. Programare online.",
        },
        en: {
          title:
            "Pilkington windshields — replacement, fitting & chip repair · Slobozia | Selena Service",
          description:
            "Pilkington Team Partner in Slobozia, Ialomița: windshield replacement, professional fitting and stone-chip repair. Fleet & CASCO workflows. Online booking.",
        },
        uk: {
          title:
            "Скло Pilkington — заміна, монтаж і ремонт сколу · Слобозія | Selena Service",
          description:
            "Офіційний Pilkington Team Partner у Слобозії: заміна лобового та бокових стекол, клейовий монтаж і ремонт сколу. Корпоративні автопарки та CASCO. Онлайн-запис.",
        },
      },
      climate: {
        ro: {
          title:
            "Încărcare freon auto R134a & R1234yf · climatizare Slobozia, Ialomița | Selena Service",
          description:
            "Încărcare și completare freon auto (agent frigorific R134a / R1234yf), verificare etanșeitate, presiuni și diagnostic climatizare la Selena Service în Slobozia. Programare online.",
        },
        en: {
          title:
            "Car A/C refrigerant refill R134a & R1234yf · Slobozia | Selena Service",
          description:
            "Automotive air conditioning service in Slobozia, Romania: R134a / R1234yf refrigerant refill, leak checks, pressures and diagnostics. Online booking.",
        },
        uk: {
          title:
            "Заправка фреону авто R134a та R1234yf · Слобозія | Selena Service",
          description:
            "Сервіс автокондиціонера у Слобозії: заправка холодоагента R134a / R1234yf, перевірка витоків і тисків, діагностика. Онлайн-запис.",
        },
      },
      mechanics: {
        ro: {
          title:
            "Service mecanică auto, revizii & reparații multimarcă · Slobozia, Ialomița | Selena Service",
          description:
            "Revizii periodice, schimb ulei și filtre, frână, suspensie și reparații mecanice multimarcă la Selena Service în Slobozia, județul Ialomița. Piese originale sau echivalente OEM. Programare online.",
        },
        en: {
          title:
            "Auto mechanics, periodic service & repairs · Slobozia, Ialomița | Selena Service",
          description:
            "Scheduled maintenance, oil changes, brakes, suspension and multi-brand mechanical repairs at Selena Service in Slobozia, Romania. Genuine or OEM-equivalent parts. Online booking.",
        },
        uk: {
          title:
            "Механіка авто, ТО та ремонт мультимарок · Слобозія | Selena Service",
          description:
            "Планове ТО, заміна оливи й фільтрів, гальма, підвіска та механічний ремонт у Selena Service (Слобозія). Оригінальні або OEM-еквівалентні запчастини. Онлайн-запис.",
        },
      },
      diagnostics: {
        ro: {
          title:
            "Diagnoză computerizată auto multimarcă · ECU, coduri DTC · Slobozia | Selena Service",
          description:
            "Citire coduri ECU (DTC), parametri live și interpretare tester pentru autoturisme multimarcă în Slobozia, Ialomița. Reducem înlocuirile inutile — clarificăm cauza înainte de reparație. Programare online.",
        },
        en: {
          title:
            "Computer diagnostics · multi-brand ECU scan, DTC codes · Slobozia | Selena Service",
          description:
            "ECU scanning, live data and fault-code interpretation before expensive parts are replaced. Multi-brand automotive diagnostics at Selena Service, Slobozia. Book online.",
        },
        uk: {
          title:
            "Комп'ютерна діагностика авто · ECU, коди DTC · Слобозія | Selena Service",
          description:
            "Зчитування кодів ЕБУ, параметри в реальному часі та інтерпретація тестера для різних марок у Слобозії. Уточнюємо причину перед дорогим ремонтом. Онлайн-запис.",
        },
      },
      oils: {
        ro: {
          title:
            "Schimb ulei motor & consultanță SAE/API/ACEA · Slobozia | Selena Service",
          description:
            "Consultanță pentru specificația corectă de ulei motor, schimb ulei și filtru și ghid practic API/SAE/ACEA în service Selena Service Slobozia, Ialomița. Selectoare oficiale producători. Programare.",
        },
        en: {
          title:
            "Engine oil change & SAE/API/ACEA consulting · Slobozia | Selena Service",
          description:
            "Motor oil specification guidance, oil & filter changes and practical API/SAE/ACEA explainers at Selena Service in Slobozia. Links to official lubricant selectors. Book online.",
        },
        uk: {
          title:
            "Заміна моторної оливи та консультація SAE/API/ACEA · Слобозія | Selena Service",
          description:
            "Підбір специфікації оливи, заміна оливи й фільтра та короткий гід API/SAE/ACEA у Selena Service (Слобозія). Посилання на офіційні селектори брендів. Онлайн-запис.",
        },
      },
      paint: {
        ro: {
          title:
            "Vopsitorie auto & tinichigerie în cabină · Slobozia, Ialomița | Selena Service",
          description:
            "Tinichigerie și vopsitorie auto profesională în cabină dedicată la Selena Service Slobozia: pregătire, stratificație, uscare controlată și finisaj. Evaluare online.",
        },
        en: {
          title:
            "Auto body paint & booth refinishing · Slobozia | Selena Service",
          description:
            "Body repair and paint booth refinishing in Slobozia: controlled prep, coatings, curing and finishing at Selena Service. Partial repairs and blends where feasible. Online estimates.",
        },
        uk: {
          title:
            "Автофарбування та рихтування в камері · Слобозія | Selena Service",
          description:
            "Рихтування та професійне фарбування в камері у Selena Service (Слобозія): підготовка, шари ЛФП, контрольоване сушіння. Часткові ремонти за можливості. Онлайн-запис.",
        },
      },
      itp: {
        ro: {
          title:
            "ITP auto și camioane · stație RAR · Slobozia, Ialomița | Selena Service",
          description:
            "Inspecție tehnică periodică pentru autoturisme și vehicule grele pe linie modernă la Selena Service Slobozia: flux organizat, intrare/ieșire separate, conform legislației. Programare online.",
        },
        en: {
          title:
            "PTI / periodic inspection · cars & trucks · Slobozia | Selena Service",
          description:
            "Roadworthiness inspections for passenger cars and heavy vehicles on a modern lane at Selena Service, Slobozia — organized workflow and compliant checks. Online booking.",
        },
        uk: {
          title:
            "ITP для легкових і вантажівок · Слобозія | Selena Service",
          description:
            "Періодичний технічний огляд на сучасній лінії в Selena Service (Слобозія): зручний потік і перевірки за вимогами. Онлайн-запис.",
        },
      },
      tachograph: {
        ro: {
          title:
            "Verificare tahograf digital · camioane & flote · Slobozia, Ialomița | Selena Service",
          description:
            "Verificări periodice tahograf digital pentru vehicule comerciale și flote — punct autorizat în rețea, la Selena Service Slobozia. Interval legal, documente și programare clară.",
        },
        en: {
          title:
            "Digital tachograph inspection · trucks & fleets · Slobozia | Selena Service",
          description:
            "Periodic digital tachograph checks for commercial vehicles and fleets at Selena Service, Slobozia — authorized workflow, paperwork guidance and scheduling.",
        },
        uk: {
          title:
            "Перевірка цифрового тахографа · вантажівки та автопарки · Слобозія | Selena Service",
          description:
            "Періодичні перевірки цифрових тахографів для комерційного транспорту та автопарків у Selena Service (Слобозія): уповноважений процес і запис.",
        },
      },
      claims: {
        ro: {
          title:
            "Constatări daune RCA/CASCO — ghid dosar și reparație în service · Slobozia | Selena Service",
          description:
            "Birou daune Selena Service Slobozia: ghid pas cu pas pentru avizare și dosar RCA (parteneri ASF), aceeași locație pentru evaluare și reparație multimarcă. Programare online.",
        },
        en: {
          title:
            "Insurance claims desk RCA/CASCO — paperwork & repair on site · Slobozia | Selena Service",
          description:
            "Selena Service Slobozia: step-by-step guidance on notice & RCA paperwork with ASF-listed insurers—same workshop for inspection and multi-brand body repair. Book online.",
        },
        uk: {
          title:
            "Прийом збитків RCA/CASCO — документи й ремонт у сервісі · Слобозія | Selena Service",
          description:
            "Selena Service (Слобозія): крок за кроком щодо повідомлення та RCA-досьє з партнерами ASF — та сама база для огляду й мультимаркового кузовного ремонту. Онлайн-запис.",
        },
      },
      vinParts: {
        ro: {
          title: "Caută piese după VIN · orientare catalog | Selena Service",
          description:
            "Instrument gratuit: decodare multimarcă după VIN (NHTSA vPIC); pentru BMW și ultimele 7 caractere, categorii și coduri OEM cu linkuri către cataloage publice. Fără prețuri afișate.",
        },
        en: {
          title: "Find parts by VIN · catalog hints | Selena Service",
          description:
            "Free tool: multi-brand decode via full VIN (NHTSA vPIC); for BMW also last seven characters, OEM-oriented categories and public catalog links. Prices are not shown.",
        },
        uk: {
          title: "Підбір запчастин за VIN · підказки каталогу | Selena Service",
          description:
            "Безкоштовний інструмент: мультимаркове декодування за повним VIN (NHTSA vPIC); для BMW також останні 7 символів, категорії запчастин і посилання на публічні каталоги. Ціни не показуємо.",
        },
      },
      notFound: {
        ro: {
          title: `Pagina nu a fost găsită | ${SITE_BRAND_DISPLAY_NAME}`,
          description:
            "Pagina solicitată nu există pe acest site. Folosiți pagina principală sau meniul de navigare pentru serviciile Selena Service.",
        },
        en: {
          title: `Page not found | ${SITE_BRAND_DISPLAY_NAME}`,
          description:
            "The page you requested does not exist on this site. Use the homepage or the navigation menu for Selena Service.",
        },
        uk: {
          title: `Сторінку не знайдено | ${SITE_BRAND_DISPLAY_NAME}`,
          description:
            "Запитаної сторінки немає на сайті. Поверніться на головну або скористайтеся меню послуг Selena Service.",
        },
      },
      terms: {
        ro: {
          title: "Termeni și Condiții · Politica de confidențialitate | Selena Service",
          description:
            `Termeni și condiții de utilizare a site-ului, informații despre cookie-uri și politica de confidențialitate conform GDPR pentru ${SITE_BRAND_DISPLAY_NAME}, Slobozia.`,
        },
        en: {
          title: "Terms & Conditions · Privacy notice | Selena Service",
          description:
            `Website terms of use, cookie information and GDPR-aligned privacy notice for ${SITE_BRAND_DISPLAY_NAME} in Slobozia, Romania.`,
        },
        uk: {
          title: "Умови використання та конфіденційність | Selena Service",
          description:
            `Умови сайту, cookie та інформація про обробку персональних даних для ${SITE_BRAND_DISPLAY_NAME} (Слобозія).`,
        },
      },
    };

    if (isNotFound) {
      const rawPath = window.location.pathname;
      const nf = seoByPage.notFound[lang] || seoByPage.notFound.ro;
      const canonicalUrl = `${baseUrl}${rawPath}${lang === "ro" ? "" : `?lang=${lang}`}`;
      document.title = nf.title;
      upsertMetaTag("name", "description", nf.description);
      upsertMetaTag("name", "robots", "noindex, follow");
      upsertMetaTag("property", "og:title", nf.title);
      upsertMetaTag("property", "og:description", nf.description);
      upsertMetaTag("property", "og:url", canonicalUrl);
      upsertMetaTag("property", "og:type", "website");
      upsertMetaTag("name", "twitter:title", nf.title);
      upsertMetaTag("name", "twitter:description", nf.description);
      applySocialPreviewMeta(baseUrl, "notFound");
      upsertLinkTag("canonical", canonicalUrl);
      upsertLinkTag("alternate", `${baseUrl}${rawPath}`, "ro-RO");
      upsertLinkTag("alternate", `${baseUrl}${rawPath}?lang=en`, "en");
      upsertLinkTag("alternate", `${baseUrl}${rawPath}?lang=uk`, "uk-UA");
      upsertLinkTag("alternate", `${baseUrl}${rawPath}`, "x-default");
      return;
    }

    if (isAppPortalRoute) {
      const ap = appPortalCopy;
      const canonicalUrl = `${baseUrl}/app${lang === "ro" ? "" : `?lang=${lang}`}`;
      document.title = ap.metaTitle;
      upsertMetaTag("name", "description", ap.metaDescription);
      upsertMetaTag("name", "robots", "noindex, follow");
      upsertMetaTag("property", "og:title", ap.metaTitle);
      upsertMetaTag("property", "og:description", ap.metaDescription);
      upsertMetaTag("property", "og:url", canonicalUrl);
      upsertMetaTag("property", "og:type", "website");
      upsertMetaTag("name", "twitter:title", ap.metaTitle);
      upsertMetaTag("name", "twitter:description", ap.metaDescription);
      applySocialPreviewMeta(baseUrl, "home");
      upsertLinkTag("canonical", canonicalUrl);
      upsertLinkTag("alternate", `${baseUrl}/app`, "ro-RO");
      upsertLinkTag("alternate", `${baseUrl}/app?lang=en`, "en");
      upsertLinkTag("alternate", `${baseUrl}/app?lang=uk`, "uk-UA");
      upsertLinkTag("alternate", `${baseUrl}/app`, "x-default");
      return;
    }

    upsertMetaTag(
      "name",
      "robots",
      "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    );

    const pagePath = isVinPartsToolRoute
      ? pathname
      : isTermsPage
      ? "/termeni-si-conditii"
      : isPilkingtonPage
        ? "/parbrize-pilkington"
        : isMechanicsPage
          ? "/mecanica-revizii"
          : isDiagnosticsPage
            ? "/diagnoza-computerizata"
            : isClimatePage
              ? "/climatizare-auto"
              : isOilsPage
                ? "/uleiuri-consultanta"
                : isPaintPage
                  ? "/vopsitorie-auto"
                  : isItpPage
                    ? "/itp-auto-camioane"
                    : isTachographPage
                      ? "/verificare-tahograf"
                      : isClaimsPage
                        ? "/constatari-daune"
                        : "/";
    const canonicalUrl = `${baseUrl}${pagePath}${lang === "ro" ? "" : `?lang=${lang}`}`;

    const pageKey = isVinPartsToolRoute
      ? "vinParts"
      : isTermsPage
      ? "terms"
      : isPilkingtonPage
      ? "pilkington"
      : isMechanicsPage
        ? "mechanics"
        : isDiagnosticsPage
          ? "diagnostics"
      : isClimatePage
        ? "climate"
        : isOilsPage
          ? "oils"
          : isPaintPage
            ? "paint"
            : isItpPage
              ? "itp"
              : isTachographPage
                ? "tachograph"
          : isClaimsPage
            ? "claims"
          : "home";
    const seo = seoByPage[pageKey][lang] || seoByPage[pageKey].ro;

    document.title = seo.title;
    upsertMetaTag("name", "description", seo.description);
    upsertMetaTag("property", "og:title", seo.title);
    upsertMetaTag("property", "og:description", seo.description);
    upsertMetaTag("property", "og:url", canonicalUrl);
    upsertMetaTag("property", "og:type", "website");
    upsertMetaTag("name", "twitter:title", seo.title);
    upsertMetaTag("name", "twitter:description", seo.description);
    applySocialPreviewMeta(baseUrl, pageKey);

    upsertLinkTag("canonical", canonicalUrl);
    upsertLinkTag("alternate", `${baseUrl}${pagePath}`, "ro-RO");
    upsertLinkTag("alternate", `${baseUrl}${pagePath}?lang=en`, "en");
    upsertLinkTag("alternate", `${baseUrl}${pagePath}?lang=uk`, "uk-UA");
    upsertLinkTag("alternate", `${baseUrl}${pagePath}`, "x-default");
  }, [
    lang,
    isPilkingtonPage,
    isMechanicsPage,
    isDiagnosticsPage,
    isClimatePage,
    isOilsPage,
    isPaintPage,
    isItpPage,
    isTachographPage,
    isClaimsPage,
    isTermsPage,
    isVinPartsToolRoute,
    isAppPortalRoute,
    appPortalCopy,
    pathname,
    isNotFound,
  ]);

  // NOTE: /admin and /desk are now handled inside SPA (no redirect to PHP).

  useEffect(() => {
    if (!canTrackCategory("analytics")) {
      return;
    }

    const apiBaseUrl = getTrackingApiBaseUrl();
    if (!apiBaseUrl) {
      return;
    }

    const body = {
      path: window.location.pathname + window.location.search,
      lang,
      referrer: document.referrer || "",
    };

    fetch(`${apiBaseUrl}/track.php`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {
      // Intentionally ignored: analytics logging should never break UX.
    });
  }, [lang, pathname]);

  useEffect(() => {
    const onTelClick = (event) => {
      const link = event.target.closest('a[href^="tel:"]');
      if (!link) return;

      const phone = (link.getAttribute("href") || "").replace(/^tel:/i, "");
      trackEvent("phone_click", {
        phone,
        source_text: (link.textContent || "").trim(),
      });
    };

    document.addEventListener("click", onTelClick);
    return () => document.removeEventListener("click", onTelClick);
  }, []);

  if (isAdminRoute) {
    return <PortalLoginPage realm="admin" title="Admin (React)" />;
  }

  if (isDeskRoute) {
    return <PortalLoginPage realm="desk" title="Desk / Recepție (React)" />;
  }

  if (isAppPortalRoute) {
    const { adminUrl, deskUrl } = getPhpPortalLoginUrls();
    return (
      <PortalEntryPage
        copy={appPortalCopy}
        adminUrl={adminUrl}
        deskUrl={deskUrl}
      />
    );
  }

  return (
    <div data-testid="app-root" className="min-h-screen bg-[#f7f8fa] text-[#0b1220] antialiased">
      <SiteHeader t={t} lang={lang} setLang={setLang} />
      <main>
        {isServiceDetailPage ? (
          <>
            <ServiceAdjacentNav t={t} pathname={pathname} lang={lang} />
            <ServiceHomeBreadcrumb t={t} lang={lang} pathname={pathname} />
          </>
        ) : null}
        {isNotFound ? (
          <NotFoundPage
            t={t}
            requestedPath={`${window.location.pathname}${window.location.search}`}
          />
        ) : isVinPartsToolRoute ? (
          <VinPartsPage t={t} pathname={pathname} />
        ) : isTermsPage ? (
          <TermsPage t={t} />
        ) : isPilkingtonPage ? (
          <PilkingtonPage t={t} />
        ) : isMechanicsPage ? (
          <MechanicsPage t={t} />
        ) : isDiagnosticsPage ? (
          <DiagnosticsPage t={t} />
        ) : isClimatePage ? (
          <ClimatePage t={t} />
        ) : isOilsPage ? (
          <OilsPage t={t} />
        ) : isPaintPage ? (
          <PaintPage t={t} />
        ) : isItpPage ? (
          <ItpPage t={t} />
        ) : isTachographPage ? (
          <TachographPage t={t} />
        ) : isClaimsPage ? (
          <ClaimsPage t={t} lang={lang} />
        ) : (
          <>
            <Hero t={t} />
            <Services t={t} />
            <WhyUs t={t} />
            <InsurerPartnersHomeSection t={t} lang={lang} />
            <Brands t={t} />
            <Booking t={t} />
            <Contact t={t} />
          </>
        )}
      </main>
      <Footer t={t} lang={lang} onCookieSettings={reopenCookieSettings} />
      <WhatsAppFloat t={t} />
      <CookieBanner t={t} openSignal={cookieOpenSignal} />
    </div>
  );
}

export default App;
